import React from "react";
import { Picker } from "./picker";
import { addMinutes, startOfDay, endOfDay, isBefore, format } from "date-fns";
import { toTime, parseTime, isSameTime, setSameTime } from "../lib/datetime";
import { setSameDay } from "../lib/datetime";
import { IconName } from "./icon";

interface TimePickerProps {
  /** Sets year, month and day of this date on the operated values */
  date?: Date;
  testID?: string;
  value?: Date;
  onChange?: (value: Date) => void;
  /** Minimum difference in minutes between time options. */
  step?: number;
  startTime?: Date;
  formatter?: (value: Date) => string;
  isBlocked?: (time: Date) => boolean;
  endTime?: Date;
  placeholder?: string;
  disabled?: boolean;
  iconBefore?: IconName;
  invalid?: boolean;
}

/**
 * Control for selecting a time.
 */
export function TimePicker(props: TimePickerProps) {
  const {
    value,
    date,
    step = 60,
    onChange = () => {},
    testID,
    startTime,
    endTime,
    formatter = (time: Date) => format(time, "HH:mm"),
    isBlocked,
    placeholder,
    disabled = false,
    iconBefore,
    invalid,
  } = props;

  const effectiveDate = date ?? new Date();

  const hours = disabled
    ? []
    : getHours({
        date: effectiveDate,
        formatter,
        step,
        startTime,
        endTime,
        isBlocked,
      });

  return (
    <Picker
      testID={testID}
      options={hours}
      placeholder={placeholder}
      disabled={disabled}
      invalid={invalid}
      title={value ? formatter(setSameTime(effectiveDate, value)) : undefined}
      value={value ? toTime(value) : undefined}
      onChange={(time) => onChange(setSameTime(effectiveDate, parseTime(time)))}
      iconBefore={iconBefore}
    />
  );
}
interface HourOption {
  value: string;
  label: string;
  disabled: boolean;
}

function getHours(params: {
  date: Date;
  step?: number;
  startTime?: Date;
  endTime?: Date;
  formatter?: (value: Date) => string;
  isBlocked?: (time: Date) => boolean;
}): HourOption[] {
  const {
    date,
    step = 30,
    isBlocked = () => false,
    startTime,
    endTime,
    formatter,
  } = params;

  const hours: HourOption[] = [];
  const start = startTime ? setSameDay(startTime, date) : startOfDay(date);

  const end = endTime ? setSameDay(endTime, date) : endOfDay(date);

  let current = start;

  while (isBefore(current, end) || isSameTime(current, end)) {
    hours.push({
      value: toTime(current),
      label: formatter ? formatter(current) : toTime(current),
      disabled: isBlocked(current),
    });

    const next = addMinutes(current, step);

    current = next;
  }

  return hours;
}
